import { Dropdown, InputRef, Menu, Space } from "antd";
import { PaginationProps } from "antd/es/pagination";
import type { ColumnType, ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AiOutlineLogout } from "react-icons/ai";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { filterFunction } from "../../common/SearchFilterComponent";
import { deleteLabel } from "../../common/common";
import useAuthorizationHook from "../../hooks/useAuthorizationHook";
import {
  IMasterInterface,
  IVehiclesLoggedInDetails,
  VehiclesInterface,
} from "../../model/MasterModel";
import { useMasterStore } from "../../stores/MasterStore";
import {
  DD_MM_YYYY_H_MM_SS,
  TWO_HUNDRED_SUCCESS_CODE,
  UserEnum,
} from "../../utils/app-common";
import { convertDateFromMillis } from "../../utils/luxon";
import ConfirmationModal from "../CommonComponent/ConfirmationModal";
import AccessControlComponent from "../RouteComponents/AccessControlComponent";
const TableComponentLoadable = React.lazy(
  () => import("../CommonComponent/TableComponent")
);

const MenuList = ({ row }: { row: IVehiclesLoggedInDetails }) => {
  const [isLoggedOutModal, setIsLoggedOutModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const { isOperationSupervisor, isManagerUser } = useAuthorizationHook();
  const { promiseInProgress } = usePromiseTracker({
    area: "logout-vehicle-area",
  });
  const UpdateVehiclesLoggedInDetailsAction = useMasterStore(
    (state) => state?.UpdateVehiclesLoggedInDetailsAction
  );
  const loggedOutResponse = useMasterStore((state) => state?.loggedOutResponse);

  const onLogoutHandler = useCallback((row: IVehiclesLoggedInDetails) => {
    setSelectedId(row?.id);
    setIsLoggedOutModal(true);
  }, []);
  const onLoggedOutConfirmedClicked = useCallback(() => {
    trackPromise(
      UpdateVehiclesLoggedInDetailsAction(selectedId),
      "logout-vehicle-area"
    );
  }, [UpdateVehiclesLoggedInDetailsAction, selectedId]);

  useEffect(() => {
    if (loggedOutResponse && loggedOutResponse === TWO_HUNDRED_SUCCESS_CODE) {
      setIsLoggedOutModal(false);
    }
    return () => {
      useMasterStore.setState({ loggedOutResponse: undefined });
    };
  }, [loggedOutResponse]);
  return (
    <>
      <Menu
        items={
          isOperationSupervisor
            ? [
              {
                key: 1,
                label: "Logout",
                disabled: !Boolean(row?.isStatus),
                icon: <AiOutlineLogout size={18} />,
                onClick: () => onLogoutHandler(row),
              },
            ]
            : []
        }
      />
      {isLoggedOutModal ? (
        <ConfirmationModal
          promiseInProgress={promiseInProgress}
          openModal={isLoggedOutModal}
          hideModal={() => setIsLoggedOutModal(false)}
          key={row?.id}
          label={`Are you sure you want to logout ${row?.vehicleName}? `}
          confirmClicked={onLoggedOutConfirmedClicked}
        />
      ) : null}
    </>
  );
};
const VehiclesLoginDetailsComponent = () => {
  const [isConfirmationModal, setIsConfirmationModal] =
    useState<boolean>(false);
  const [currentRow, setCurrentRow] = useState<VehiclesInterface | undefined>();
  const vehiclesLoggedInDetails = useMasterStore(
    (state: IMasterInterface) => state?.vehiclesLoggedInDetails
  );
  const totalNumberOfVehicleLogins = useMasterStore(
    (state: IMasterInterface) => state?.totalNumberOfVehicleLogins
  );
  const GetVehiclesLoggedInDetailsAction = useMasterStore(
    (state: IMasterInterface) => state?.GetVehiclesLoggedInDetailsAction
  );
  const [pageNumber, setPageNumber] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { promiseInProgress } = usePromiseTracker({
    area: "vehicle-list-area",
  });
  const { promiseInProgress: logoutVehicleArea } = usePromiseTracker({
    area: "logout-vehicle-area",
  });

  const searchInput = useRef<InputRef>(null);
  const getColumnSearchProps = (
    dataIndex: any
  ): ColumnType<VehiclesInterface> => filterFunction(dataIndex, searchInput);
  const deleteVehicleByIdAction = useMasterStore(
    (state: IMasterInterface) => state?.DeleteVehicleByIdAction
  );

  const getVehicleData = useCallback(() => {
    trackPromise(
      GetVehiclesLoggedInDetailsAction(pageNumber, pageSize),
      "vehicle-list-area"
    );
  }, [GetVehiclesLoggedInDetailsAction, pageNumber, pageSize]);

  useEffect(() => {
    getVehicleData();
  }, [getVehicleData, pageNumber, pageSize]);

  const total: number = React.useMemo(
    () => totalNumberOfVehicleLogins,
    [totalNumberOfVehicleLogins]
  );

  const vehiclesColumns: ColumnsType<VehiclesInterface> = [
    {
      title: "vehicle Name",
      dataIndex: "vehicleName",
      width: 100,
      key: "vehicleName",
      ...getColumnSearchProps("vehicleName"),
    },
    {
      title: "plate Number",
      width: 100,
      dataIndex: "plateNumber",
      key: "plateNumber",
      ...getColumnSearchProps("plateNumber"),
    },
    {
      title: "employeeName",
      width: 100,
      dataIndex: "employeeName",
      key: "employeeName",
      ...getColumnSearchProps("employeeName"),
    },
    {
      title: "plate Number",
      width: 100,
      dataIndex: "plateNumber",
      key: "plateNumber",
      ...getColumnSearchProps("plateNumber"),
    },
    {
      title: "login Status",
      dataIndex: "loginStatus",
      width: 100,
      key: "loginStatus",
      ...getColumnSearchProps("loginStatus"),
    },
    {
      title: "login Time",
      width: 100,
      dataIndex: "loginTime",
      key: "loginTime",
      ...getColumnSearchProps("loginTime"),
      render: (loginTime: number) =>
        loginTime !== 0 && convertDateFromMillis(loginTime, DD_MM_YYYY_H_MM_SS),
    },
    {
      title: "logout Time",
      width: 100,
      dataIndex: "logoutTime",
      key: "logoutTime",
      ...getColumnSearchProps("logoutTime"),
      render: (logoutTime: number) =>
        logoutTime !== 0 &&
        convertDateFromMillis(logoutTime, DD_MM_YYYY_H_MM_SS),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (row: any) => {
        return (
          <AccessControlComponent
            access={[
              UserEnum.MANAGER,
              UserEnum.OPERATION_SUPERVISOR,
              UserEnum.OPERATION_SUPERVISOR_CASH_COLLECTOR,
              UserEnum.FLEET_SUPERVISOR,
              UserEnum.OPERATION_CS_SUPERVISOR,
              UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,
              UserEnum.OPS_MANAGER,
              UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
            ]}
          >
            <Space size="middle">
              <Dropdown.Button
                overlay={() => <MenuList row={row} />}
              ></Dropdown.Button>
            </Space>
          </AccessControlComponent>
        );
      },
    },
  ];

  const onPaginationChange: PaginationProps["onChange"] = (page: number) => {
    setPageNumber(page - 1);
    setCurrentPageNumber(page);
  };
  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (
    current: number,
    pageSize: number
  ) => {
    setPageSize(pageSize);
  };

  const onConfirmedClicked = useCallback(() => {
    setCurrentRow(currentRow);
    trackPromise(deleteVehicleByIdAction(currentRow?.vehId), "logout-vehicle");
    setIsConfirmationModal(false);
  }, [currentRow, deleteVehicleByIdAction]);

  const onResetClicked = useCallback(() => {
    setCurrentPageNumber(1);
    getVehicleData();
  }, [getVehicleData]);

  return (
    <>
      <TableComponentLoadable
        columns={vehiclesColumns}
        data={vehiclesLoggedInDetails}
        isLoading={promiseInProgress}
        onPaginationChange={onPaginationChange}
        onShowSizeChange={onShowSizeChange}
        total={total}
        current={currentPageNumber}
        onRefreshHandle={onResetClicked}
      />
      {isConfirmationModal ? (
        <ConfirmationModal
          promiseInProgress={logoutVehicleArea}
          openModal={isConfirmationModal}
          confirmClicked={() => onConfirmedClicked()}
          hideModal={() => setIsConfirmationModal(false)}
          label={deleteLabel(`"${currentRow?.driverName!}"`)}
        ></ConfirmationModal>
      ) : null}
    </>
  );
};

VehiclesLoginDetailsComponent.propTypes = {};
export default VehiclesLoginDetailsComponent;
